import { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, ExternalLink } from "lucide-react";
import { api, absUrl, intakeLinkStore } from "./api";
import { StatusPill, CopyField, LoadError } from "./ui.jsx";
import { IntakeAnswersButton } from "./IntakeAnswers.jsx";
import { VisualUpdate } from "./VisualUpdate.jsx";
import { ClientRequests } from "./ClientRequests.jsx";
import { RequestChanges } from "./RequestChanges.jsx";
import { UnresolvedRequests } from "./UnresolvedRequests.jsx";
import { PublishPanel } from "./PublishPanel.jsx";
import { EmailLog } from "./EmailLog.jsx";

const emailToast = (rec, what) => {
  if (!rec) return;
  if (rec.status === "sent") toast.success(`${what} emailed to ${rec.to}`);
  else if (rec.status === "skipped") toast.info(`${what} ready — ${rec.detail} Copy the link below.`);
  else toast.error(`${what} email ${rec.status}: ${rec.detail}`);
};

const EVENT_LABEL = {
  job_created: "Job created",
  intake_submitted: "Client submitted intake",
  ready_for_generation: "Ready for generation",
  generation_started: "Generation started",
  generation_completed: "Website generated",
  generation_failed: "Generation failed",
  intake_corrected: "Intake corrected by Alias",
  contact_corrected: "Phone number corrected by Alias (applies to later versions)",
  contact_correction_backfilled: "Earlier phone correction recorded by Alias",
  alias_change_requested: "Alias requested website changes",
  alias_change_completed: "Alias changes applied — new version for QA",
  alias_change_failed: "Alias change request could not be processed",
  client_review_revoked: "Client review link cancelled",
  request_decision_recorded: "Alias decision recorded for a client request that was not completed",
  intake_correction_reverted: "Intake correction reverted",
  approved: "Approved for client review",
  client_review_created: "Client review link issued",
  client_approved: "Client approved",
  client_requested_changes: "Client requested changes",
  revision_generation_completed: "Revision generated",
  changes_required: "Alias requested changes",
  alias_visual_update: "Alias QA visual update (no AI, no client revision)",
};

export default function JobDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  const [reviewLink, setReviewLink] = useState(null);
  const [intakeLink, setIntakeLink] = useState(intakeLinkStore.get(id));
  const [loadErr, setLoadErr] = useState(null);

  const load = useCallback(async () => {
    try { setD(await api.jobDetail(id)); setLoadErr(null); }
    catch (e) { setLoadErr(e); toast.error(e.message); }
  }, [id]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.previewSession().catch(() => {}); }, []);
  const generating = Boolean(d?.summary?.generating);
  useEffect(() => {
    if (!generating) return undefined;
    const t = setInterval(load, 10000);
    return () => clearInterval(t);
  }, [generating, load]);

  const run = async (fn, okMsg) => {
    setBusy(true);
    try {
      const r = await fn();
      if (okMsg) toast.success(okMsg);
      await load();
      return r;
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };

  if (!d && loadErr) return <LoadError err={loadErr} onRetry={load} onBack={() => nav("/")} />;
  if (!d) return <div className="min-h-screen bg-slate-950 text-slate-400 grid place-items-center">Loading…</div>;

  const s = d.summary;
  const key = s.nextAction.key;
  const preview = absUrl(d.qa.previewPath);

  const confirmHistorical = () => {
    if (!d.emailAutomation?.historical) return false;
    return window.confirm("This job was created before email automation was activated. Send this email to the client manually? This is recorded in the job history.") ? true : null;
  };

  const primary = async () => {
    if (key === "generate") return run(() => api.generate(id), "Generation started");
    if (key === "approve") {
      const r = await run(() => api.approve(id), "Approved for client review");
      if (r) { setReviewLink(r.reviewLink); emailToast(r.reviewEmail, "Review link"); }
      return r;
    }
    if (key === "revise") return run(() => api.revise(id), "Revision generated");
    if (key === "issue_review") {
      const hist = confirmHistorical();
      if (hist === null) return;
      const r = await run(() => api.issueReview(id, hist));
      if (r) { setReviewLink(r.reviewLink); emailToast(r.reviewEmail, `${r.stage === "final" ? "Final" : "Client"} review link`); }
    }
    if (key === "copy_intake_link") {
      let link = intakeLink;
      if (!link) { const r = await run(() => api.reissueIntake(id)); link = r && r.intakeLink; }
      if (link) { intakeLinkStore.set(id, link); setIntakeLink(link); toast.success("Intake link ready to copy"); }
    }
  };

  const showPrimaryBtn = !["ready_to_publish", "none", "completed", "reverify"].includes(key);
  const resendIntake = async () => {
    const hist = confirmHistorical();
    if (hist === null) return;
    const r = await run(() => api.sendIntakeEmail(id, hist));
    if (r) { intakeLinkStore.set(id, r.intakeLink); setIntakeLink(r.intakeLink); emailToast(r.intakeEmail, "New intake link"); }
  };

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100">
      <header className="sticky top-0 z-10 backdrop-blur bg-slate-950/80 border-b border-slate-800">
        <div className="mx-auto max-w-3xl px-4 py-4 flex items-center gap-3">
          <button onClick={() => nav("/")} data-testid="back-to-jobs" className="rounded-lg p-2 text-slate-400 hover:text-slate-100 hover:bg-slate-800/60 transition">
            <ArrowLeft className="h-4 w-4" />
          </button>
          <div className="min-w-0">
            <p className="font-semibold truncate">{s.displayName.name || "Unnamed job"}</p>
            <p className="text-xs text-slate-500 font-mono">{s.jobId}</p>
          </div>
          <div className="ml-auto"><StatusPill status={s.status} live={key === "completed"} reverify={key === "reverify"} /></div>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-4 py-5 space-y-5">
        {/* Next action */}
        <section className="rounded-2xl bg-slate-900/70 ring-1 ring-slate-800 p-4">
          <p className="text-[11px] uppercase tracking-wide text-slate-400 mb-2">Next action</p>
          <div className="flex items-center justify-between gap-3">
            <p className="text-lg font-medium">{s.nextAction.label}</p>
            {showPrimaryBtn && (
              <button
                onClick={primary}
                disabled={busy}
                data-testid={`action-${key}`}
                className="shrink-0 rounded-xl bg-amber-500 px-4 py-2.5 font-semibold text-slate-950 hover:bg-amber-400 disabled:opacity-60 transition-colors"
              >
                {busy ? (key === "generate" ? "Generating…" : "Working…") : s.nextAction.label}
              </button>
            )}
            {key === "completed" && (
              <span data-testid="job-completed" className="rounded-xl bg-emerald-500/15 text-emerald-300 ring-1 ring-emerald-500/30 px-3 py-2 text-sm font-semibold">Live · Completed</span>
            )}
            {key === "reverify" && (
              <span data-testid="reverify-needed" className="rounded-xl bg-amber-500/15 text-amber-300 ring-1 ring-amber-500/30 px-3 py-2 text-sm font-semibold">Alias re-verification needed before publishing</span>
            )}
            {key === "ready_to_publish" && (
              <span data-testid="ready-to-publish" className="rounded-xl bg-emerald-500/15 text-emerald-300 ring-1 ring-emerald-500/30 px-3 py-2 text-sm font-semibold">Ready to publish</span>
            )}
          </div>
          {s.lastError && (
            <p data-testid="generation-error" className="mt-3 rounded-lg bg-red-500/10 ring-1 ring-red-500/30 px-3 py-2 text-sm text-red-300 break-words">
              Last attempt failed: {s.lastError}. The client's intake answers are saved unchanged — you can retry generation.
            </p>
          )}
          {s.generating && (
            <p data-testid="generation-in-progress" className="mt-3 text-sm text-slate-400">Generating the website — this usually takes 1–3 minutes. This page updates automatically; you can safely leave it.</p>
          )}
          {key === "copy_intake_link" && intakeLink && (
            <div className="mt-3"><CopyField label="Intake link — send to client" value={intakeLink} testid="detail-intake-link" /></div>
          )}
          {key === "copy_intake_link" && s.clientEmail && (
            <button onClick={resendIntake} disabled={busy} data-testid="resend-intake-email"
              className="mt-3 rounded-lg px-3 py-1.5 text-sm text-amber-200 ring-1 ring-amber-500/40 hover:bg-amber-500/10 transition-colors">
              Email a new intake link to {s.clientEmail} (the old link stops working)
            </button>
          )}
          {reviewLink && (
            <div className="mt-3"><CopyField label="Client review link — send to client" value={reviewLink} testid="detail-review-link" /></div>
          )}
          {d.activeReview && (
            <div className="mt-3 flex flex-wrap items-center justify-between gap-2 text-sm text-slate-400" data-testid="active-review">
              <span>Active client link for {d.activeReview.version} — expires {new Date(d.activeReview.expiresAt).toLocaleDateString()}. Issuing a new link cancels this one.</span>
              <button onClick={async () => { if (window.confirm("Cancel the client's current review link? They will need a new link from you.")) { await run(() => api.revokeReview(id), "Client link cancelled"); setReviewLink(null); } }}
                disabled={busy} data-testid="revoke-review"
                className="rounded-lg px-3 py-1.5 text-rose-300 ring-1 ring-rose-500/40 hover:bg-rose-500/10 transition-colors">Revoke client link</button>
            </div>
          )}
        </section>

        <EmailLog emails={d.emails || []} configured={d.mailConfigured} automation={d.emailAutomation} />

        {d.revisionReady ? (
          <section className="rounded-2xl bg-emerald-500/10 ring-2 ring-emerald-500/50 p-4 space-y-3" data-testid="revision-ready">
            <p className="text-lg font-semibold text-emerald-200">Your revised website is ready to review.</p>
            <p className="text-sm text-slate-300">Open the preview and check each change the client asked for before sending the website back to them. Nothing has been sent to the client.</p>
            {preview && (
              <a href={preview} target="_blank" rel="noopener noreferrer" data-testid="revision-ready-preview"
                className="flex items-center justify-center gap-2 rounded-xl bg-emerald-400 py-3 font-semibold text-slate-950 hover:bg-emerald-300 transition-colors">
                <ExternalLink className="h-4 w-4" /> Open preview ({d.revisionReady.version})
              </a>
            )}
            <UnresolvedRequests jobId={s.jobId} items={d.unresolvedRequests} options={d.decisionOptions} onDone={load} />
          </section>
        ) : d.unresolvedRequests && d.unresolvedRequests.length > 0 && s.status !== "CLIENT_APPROVED" && (
          <section className="rounded-2xl bg-amber-500/5 ring-1 ring-amber-500/40 p-4" data-testid="unresolved-card">
            <p className="text-[11px] uppercase tracking-wide text-amber-300/90 mb-2">Client requests not completed</p>
            <UnresolvedRequests jobId={s.jobId} items={d.unresolvedRequests} options={d.decisionOptions} onDone={load} />
          </section>
        )}
        {s.status === "CLIENT_APPROVED" && d.clientApprovedNotCompleted > 0 && (
          <p className="rounded-xl bg-amber-500/10 ring-1 ring-amber-500/30 px-3 py-2 text-sm text-amber-200" data-testid="approved-not-completed">
            Client approved — {d.clientApprovedNotCompleted} requested change{d.clientApprovedNotCompleted > 1 ? "s were" : " was"} not made (the client was shown this before approving).
          </p>
        )}
        {s.status === "CLIENT_APPROVED" && <PublishPanel jobId={s.jobId} />}

        {/* Facts */}
        <section className="grid grid-cols-2 gap-3">
          <Fact label="Client email" value={s.clientEmail || "—"} testid="fact-client-email" className="col-span-2" />
          {s.intakeSubmitted && (
            <Fact label="Colour preference" testid="fact-colour-pref" className="col-span-2"
              value={d.colourPreference?.choice === "suggested"
                ? <span className="inline-flex items-center gap-2"><span className="inline-block h-3.5 w-3.5 rounded-full ring-1 ring-white/30" style={{ background: d.colourPreference.hex }} />{d.colourPreference.name}</span>
                : d.colourPreference ? "No preference" : "Not asked (older intake)"} />
          )}
          {d.effectivePhone && (
            <Fact label="Effective phone number" testid="fact-effective-phone" className="col-span-2"
              value={<>
                {d.effectivePhone.phone}
                <span className="block mt-1 text-xs font-normal text-slate-400" data-testid="fact-effective-phone-source">
                  {d.effectivePhone.correction
                    ? `Corrected by Alias on ${new Date(d.effectivePhone.correction.createdAt).toLocaleString()} in ${d.effectivePhone.correction.version} (${d.effectivePhone.correction.reason}). Original intake: ${d.effectivePhone.intakePhone}`
                    : "From the client's intake"}
                </span>
                {d.effectivePhone.mismatch && (
                  <span className="block mt-2 text-xs font-normal text-amber-300" data-testid="fact-effective-phone-mismatch">
                    {d.effectivePhone.currentVersion} shows {d.effectivePhone.currentVersionPhone}, which is not a recorded correction. A new generation would use {d.effectivePhone.phone}.
                  </span>
                )}
              </>} />
          )}
          <Fact label="Intake" value={s.intakeSubmitted ? "Submitted" : "Awaiting"} testid="fact-intake" />
          <Fact label="Current version" value={s.currentVersion || "—"} testid="fact-version" />
          <Fact label="Revision" value={`${s.revisionUsed} / ${s.revisionAllowed} used`} testid="fact-revision" />
          <Fact label="Validation errors" value={d.qa.validation ? String(d.qa.validation.errors) : "—"} testid="fact-validation" />
        </section>

        {s.intakeSubmitted && <IntakeAnswersButton jobId={s.jobId} />}

        <ClientRequests requests={d.clientRequests} />

        {d.needsReviewDetail && d.needsReviewDetail.length > 0 ? (
          <section className="rounded-2xl bg-slate-900/70 ring-1 ring-slate-800 p-4">
            <p className="text-[11px] uppercase tracking-wide text-slate-400 mb-2">Needs review</p>
            <ul className="space-y-2" data-testid="needs-review">
              {d.needsReviewDetail.map((n, i) => (
                <li key={i} data-testid={`needs-review-item-${i}`} className="text-sm">
                  <span className={`mr-2 rounded px-1.5 py-0.5 text-[10px] font-semibold ${n.code === "CLIENT_REQ_APPLIED" ? "bg-emerald-500/15 text-emerald-300" : "bg-amber-500/15 text-amber-300"}`}>
                    {n.code === "CLIENT_REQ_APPLIED" ? "APPLIED" : n.code === "CLIENT_REQ_REVIEW" ? "ALIAS REVIEW" : n.code}
                  </span>
                  <span className="text-slate-300 break-words">{n.message}</span>
                </li>
              ))}
            </ul>
          </section>
        ) : d.qa.needsReview && d.qa.needsReview.length > 0 && (
          <section className="rounded-2xl bg-slate-900/70 ring-1 ring-slate-800 p-4">
            <p className="text-[11px] uppercase tracking-wide text-slate-400 mb-2">Needs review</p>
            <div className="flex flex-wrap gap-2" data-testid="needs-review">
              {d.qa.needsReview.map((n) => (
                <span key={n} className="rounded-lg bg-slate-800/70 px-2.5 py-1 text-xs text-slate-300">{n}</span>
              ))}
            </div>
          </section>
        )}

        {preview && (
          <a href={preview} target="_blank" rel="noopener noreferrer" data-testid="view-preview"
             className="flex items-center justify-center gap-2 rounded-2xl bg-slate-100 py-3 font-semibold text-slate-950 hover:bg-white transition-colors">
            <ExternalLink className="h-4 w-4" /> View preview {s.currentVersion ? `(${s.currentVersion})` : ""}
          </a>
        )}

        {s.status === "AWAITING_ALIAS_QA" && s.currentVersion && !s.generating && (
          <RequestChanges jobId={s.jobId} version={s.currentVersion} onDone={load} />
        )}
        {s.status === "AWAITING_ALIAS_QA" && s.currentVersion && !s.generating && (
          <VisualUpdate jobId={s.jobId} version={s.currentVersion} onDone={load} />
        )}

        {/* Timeline */}
        <section className="rounded-2xl bg-slate-900/70 ring-1 ring-slate-800 p-4">
          <p className="text-[11px] uppercase tracking-wide text-slate-400 mb-3">Workflow history</p>
          <ol className="space-y-3" data-testid="timeline">
            {[...d.events].reverse().map((e, i) => (
              <li key={i} className="flex gap-3">
                <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-amber-400/80" />
                <div className="min-w-0">
                  <p className="text-sm">{EVENT_LABEL[e.event] || e.event}{e.version ? ` · ${e.version}` : ""}</p>
                  {e.event === "alias_visual_update" && e.meta?.reason && (
                    <p className="text-xs text-slate-400 break-words">Reason: {e.meta.reason}</p>
                  )}
                  {e.event === "alias_visual_update" && (e.meta?.changes || []).map((c) => (
                    <p key={c} className="text-xs text-emerald-300/80 break-words">Applied: {c}</p>
                  ))}
                  <p className="text-[11px] text-slate-500">{new Date(e.ts).toLocaleString()} · {e.actor}</p>
                </div>
              </li>
            ))}
          </ol>
        </section>
      </main>
    </div>
  );
}

function Fact({ label, value, testid, className = "" }) {
  return (
    <div className={`rounded-2xl bg-slate-900/60 ring-1 ring-slate-800 p-4 ${className}`}>
      <p className="text-[11px] uppercase tracking-wide text-slate-400">{label}</p>
      <p className="mt-1 font-medium break-words" data-testid={testid}>{value}</p>
    </div>
  );
}
